<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useTracking } from 'dashboard/composables/useTracking';
import { REDIRECTING_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import {
  buildRedirectTrackingProperties,
  redirectingCompanyName,
  resolveRedirectDelay,
  resolveRedirectDestination,
} from '../utils/redirecting';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { track } = useTracking();
const phraseIndex = ref(0);
let redirectTimer = null;
let phraseTimer = null;

const destination = computed(() =>
  resolveRedirectDestination({
    accountId: route.params.accountId,
    query: route.query,
  })
);

const companyName = computed(
  () => redirectingCompanyName({ query: route.query }) || t('REDIRECTING.BRAND')
);

const phraseTexts = computed(() => [
  t('REDIRECTING.PHRASES.STEP_ONE', { companyName: companyName.value }),
  t('REDIRECTING.PHRASES.STEP_TWO'),
  t('REDIRECTING.PHRASES.STEP_THREE'),
  t('REDIRECTING.PHRASES.STEP_FOUR'),
  t('REDIRECTING.PHRASES.STEP_FIVE'),
]);

const activePhrase = computed(() => phraseTexts.value[phraseIndex.value]);

const clearTimers = () => {
  if (redirectTimer) window.clearTimeout(redirectTimer);
  if (phraseTimer) window.clearInterval(phraseTimer);
  redirectTimer = null;
  phraseTimer = null;
};

const replaceRoute = () => {
  const navigation = router.replace(destination.value);
  if (typeof navigation?.catch === 'function') navigation.catch(() => {});
};

const startPhraseRotation = delayMs => {
  const intervalMs = Math.max(
    Math.floor(delayMs / phraseTexts.value.length),
    400
  );

  phraseTimer = window.setInterval(() => {
    if (phraseIndex.value >= phraseTexts.value.length - 1) {
      window.clearInterval(phraseTimer);
      phraseTimer = null;
      return;
    }

    phraseIndex.value += 1;
  }, intervalMs);
};

onMounted(() => {
  const delayMs = resolveRedirectDelay({ query: route.query });
  track(
    REDIRECTING_EVENTS.PAGE_VIEWED,
    buildRedirectTrackingProperties(route.query)
  );
  startPhraseRotation(delayMs);
  redirectTimer = window.setTimeout(replaceRoute, delayMs);
});

onBeforeUnmount(() => {
  clearTimers();
});
</script>

<template>
  <section
    class="grid h-full place-items-center bg-lais-bg-page px-4 font-redHatText"
  >
    <div
      class="phrasing-loading-cozy grid w-full max-w-[520px] gap-5 rounded-lais border border-lais-border-soft bg-white p-6 text-center shadow-lais-card"
    >
      <div
        class="mx-auto grid size-14 place-items-center rounded-full bg-lais-primary-50 text-lais-primary"
      >
        <span
          class="i-lucide-loader-2 size-7 animate-spin"
          aria-hidden="true"
        />
      </div>

      <div>
        <h1 class="text-[20px] font-semibold text-lais-text">
          {{ t('REDIRECTING.TITLE') }}
        </h1>
        <p
          class="mt-2 min-h-5 text-[14px] font-semibold text-lais-text-body"
          aria-live="polite"
        >
          {{ activePhrase }}
        </p>
        <p class="mt-2 text-[12px] text-lais-text-muted">
          {{ t('REDIRECTING.SUBTITLE') }}
        </p>
      </div>

      <ol class="grid gap-2 text-left">
        <li
          v-for="(phrase, index) in phraseTexts"
          :key="phrase"
          class="flex items-center gap-2 rounded-lais-sm px-2 py-1.5 text-[12px]"
          :class="
            index <= phraseIndex
              ? 'bg-lais-primary-50 text-lais-primary'
              : 'bg-lais-bg-soft text-lais-text-muted'
          "
        >
          <span
            class="grid size-5 shrink-0 place-items-center rounded-full bg-white text-[10px] font-bold"
          >
            {{ index + 1 }}
          </span>
          <span class="truncate">{{ phrase }}</span>
        </li>
      </ol>
    </div>
  </section>
</template>
